// Tiny schema combinators: one definition gives runtime validation (here) and a JSON Schema
// (emitted for PHP's MaxSchemaValidator and for agents). Only the JSON Schema subset that
// PHP implements may be produced: type, properties, required, additionalProperties:false,
// enum, const, minLength, maxLength, pattern, minimum, maximum, minItems, maxItems, items,
// oneOf with a const discriminator, anyOf, $ref/$defs, maxProperties, propertyNames, and
// x-max-items-message (the message for too many items).

export interface Issue {
  path: string
  message: string
}

export interface Ctx {
  issues: Issue[]
  defs: Record<string, Schema>
  depth: number
  maxDepth: number
  maxIssues: number
}

export interface JsonCtx {
  defs: Record<string, Schema>
  emitted: Record<string, unknown>
}

export interface Schema {
  description?: string
  check (value: unknown, path: string, ctx: Ctx): boolean
  json (ctx: JsonCtx): Record<string, unknown>
}

function push (ctx: Ctx, path: string, message: string): false {
  if (ctx.issues.length < ctx.maxIssues) {
    ctx.issues.push({ path: path || '$', message })
  }
  return false
}

function typeOf (value: unknown): string {
  if (value === null) return 'null'
  if (Array.isArray(value)) return 'array'
  return typeof value
}

function describe<T extends Schema> (schema: T, description?: string): T {
  if (description) schema.description = description
  return schema
}

function withDescription (out: Record<string, unknown>, schema: Schema): Record<string, unknown> {
  return schema.description ? { description: schema.description, ...out } : out
}

export interface StringOpts {
  min?: number
  max: number
  pattern?: RegExp
  description?: string
}

export function string (opts: StringOpts): Schema {
  if (opts.pattern && opts.pattern.flags) {
    throw new Error(`Schema pattern /${opts.pattern.source}/ must not use flags; JSON Schema cannot express them`)
  }
  const schema: Schema = {
    check (value, path, ctx) {
      if (typeof value !== 'string') return push(ctx, path, `expected string, got ${typeOf(value)}`)
      const length = [...value].length
      if (opts.min !== undefined && length < opts.min) return push(ctx, path, `must be at least ${opts.min} characters`)
      if (length > opts.max) return push(ctx, path, `must be at most ${opts.max} characters (got ${length})`)
      if (opts.pattern && !opts.pattern.test(value)) return push(ctx, path, `does not match the required format`)
      return true
    },
    json () {
      return withDescription({
        type: 'string',
        ...(opts.min !== undefined ? { minLength: opts.min } : {}),
        maxLength: opts.max,
        ...(opts.pattern ? { pattern: opts.pattern.source } : {})
      }, schema)
    }
  }
  return describe(schema, opts.description)
}

export interface NumberOpts {
  min?: number
  max?: number
  integer?: boolean
  description?: string
}

export function number (opts: NumberOpts = {}): Schema {
  const schema: Schema = {
    check (value, path, ctx) {
      if (typeof value !== 'number' || !Number.isFinite(value)) return push(ctx, path, `expected a finite number, got ${typeOf(value)}`)
      if (opts.integer && !Number.isInteger(value)) return push(ctx, path, 'expected an integer')
      if (opts.min !== undefined && value < opts.min) return push(ctx, path, `must be >= ${opts.min}`)
      if (opts.max !== undefined && value > opts.max) return push(ctx, path, `must be <= ${opts.max}`)
      return true
    },
    json () {
      return withDescription({
        type: opts.integer ? 'integer' : 'number',
        ...(opts.min !== undefined ? { minimum: opts.min } : {}),
        ...(opts.max !== undefined ? { maximum: opts.max } : {})
      }, schema)
    }
  }
  return describe(schema, opts.description)
}

export function boolean (description?: string): Schema {
  const schema: Schema = {
    check (value, path, ctx) {
      return typeof value === 'boolean' ? true : push(ctx, path, `expected boolean, got ${typeOf(value)}`)
    },
    json () {
      return withDescription({ type: 'boolean' }, schema)
    }
  }
  return describe(schema, description)
}

export function nullValue (): Schema {
  return {
    check (value, path, ctx) {
      return value === null ? true : push(ctx, path, `expected null, got ${typeOf(value)}`)
    },
    json () {
      return { type: 'null' }
    }
  }
}

export function literal (value: string | number | boolean, description?: string): Schema {
  const schema: Schema = {
    check (v, path, ctx) {
      return v === value ? true : push(ctx, path, `expected ${JSON.stringify(value)}`)
    },
    json () {
      return withDescription({ const: value }, schema)
    }
  }
  return describe(schema, description)
}

export function enumOf (values: readonly (string | number)[], description?: string): Schema {
  const schema: Schema = {
    check (v, path, ctx) {
      return values.includes(v as string | number)
        ? true
        : push(ctx, path, `must be one of: ${values.map(x => JSON.stringify(x)).join(', ')}`)
    },
    json () {
      return withDescription({ enum: [...values] }, schema)
    }
  }
  return describe(schema, description)
}

export interface ArrayOpts {
  min?: number
  max: number
  /** Message when there are more than max items, e.g. naming the limit; {got} is the count. Emitted as x-max-items-message */
  maxMessage?: string
  description?: string
}

export function array (item: Schema, opts: ArrayOpts): Schema {
  const schema: Schema = {
    check (value, path, ctx) {
      if (!Array.isArray(value)) return push(ctx, path, `expected array, got ${typeOf(value)}`)
      if (opts.min !== undefined && value.length < opts.min) return push(ctx, path, `must contain at least ${opts.min} item(s)`)
      if (value.length > opts.max) return push(ctx, path, opts.maxMessage ? opts.maxMessage.replace('{got}', String(value.length)) : `must contain at most ${opts.max} items (got ${value.length})`)
      let ok = true
      for (let i = 0; i < value.length; i++) {
        if (ctx.issues.length >= ctx.maxIssues) return false
        if (!item.check(value[i], `${path}[${i}]`, ctx)) ok = false
      }
      return ok
    },
    json (jctx) {
      return withDescription({
        type: 'array',
        items: item.json(jctx),
        ...(opts.min !== undefined ? { minItems: opts.min } : {}),
        maxItems: opts.max,
        ...(opts.maxMessage ? { 'x-max-items-message': opts.maxMessage } : {})
      }, schema)
    }
  }
  return describe(schema, opts.description)
}

export interface ObjectOpts {
  required?: string[]
  description?: string
  refine?: (value: Record<string, unknown>, path: string, report: (subPath: string, message: string) => void) => void
}

export function object (props: Record<string, Schema>, opts: ObjectOpts = {}): Schema & { props: Record<string, Schema>, required: string[] } {
  const required = opts.required ?? []
  const schema = {
    props,
    required,
    check (value: unknown, path: string, ctx: Ctx) {
      if (typeOf(value) !== 'object') return push(ctx, path, `expected object, got ${typeOf(value)}`)
      const obj = value as Record<string, unknown>
      let ok = true
      for (const key of required) {
        if (!(key in obj) || obj[key] === undefined) {
          ok = push(ctx, path ? `${path}.${key}` : key, 'is required')
        }
      }
      for (const key of Object.keys(obj)) {
        const sub = path ? `${path}.${key}` : key
        const propSchema = props[key]
        if (!propSchema) {
          ok = push(ctx, sub, `unknown property; allowed: ${Object.keys(props).join(', ')}`)
          continue
        }
        if (obj[key] === undefined) continue
        if (!propSchema.check(obj[key], sub, ctx)) ok = false
      }
      if (ok && opts.refine) {
        opts.refine(obj, path, (subPath, message) => {
          ok = push(ctx, subPath ? (path ? `${path}.${subPath}` : subPath) : path, message)
        })
      }
      return ok
    },
    json (jctx: JsonCtx) {
      const properties: Record<string, unknown> = {}
      for (const [key, s] of Object.entries(props)) {
        properties[key] = s.json(jctx)
      }
      return withDescription({
        type: 'object',
        properties,
        ...(required.length ? { required } : {}),
        additionalProperties: false
      }, schema as Schema)
    }
  }
  return describe(schema, opts.description)
}

export interface RecordOpts {
  maxKeys: number
  keyPattern: RegExp
  description?: string
}

/** Object with arbitrary keys matching keyPattern, e.g. table rows. */
export function record (value: Schema, opts: RecordOpts): Schema {
  if (opts.keyPattern.flags) {
    throw new Error(`Schema key pattern /${opts.keyPattern.source}/ must not use flags; JSON Schema cannot express them`)
  }
  const schema: Schema = {
    check (v, path, ctx) {
      if (typeOf(v) !== 'object') return push(ctx, path, `expected object, got ${typeOf(v)}`)
      const obj = v as Record<string, unknown>
      const keys = Object.keys(obj)
      if (keys.length > opts.maxKeys) return push(ctx, path, `must have at most ${opts.maxKeys} keys`)
      let ok = true
      for (const key of keys) {
        if (!opts.keyPattern.test(key)) {
          ok = push(ctx, `${path}.${key}`, 'invalid key')
          continue
        }
        if (!value.check(obj[key], `${path}.${key}`, ctx)) ok = false
      }
      return ok
    },
    json (jctx) {
      return withDescription({
        type: 'object',
        maxProperties: opts.maxKeys,
        propertyNames: { pattern: opts.keyPattern.source },
        additionalProperties: value.json(jctx)
      }, schema)
    }
  }
  return describe(schema, opts.description)
}

export function anyOf (options: Schema[], description?: string): Schema {
  const schema: Schema = {
    check (value, path, ctx) {
      // When an option takes the value's type but fails inside it (an operator with too many
      // arguments), its own issues say more than "does not match any allowed type"
      let closest: Issue[] | null = null
      for (const option of options) {
        const probe: Ctx = { ...ctx, issues: [] }
        if (option.check(value, path, probe)) return true
        if (!closest && probe.issues.length && !probe.issues.every(i => i.path === (path || '$') && /^(expected |does not match any allowed type)/.test(i.message))) closest = probe.issues
      }
      if (closest) {
        for (const issue of closest) push(ctx, issue.path === '$' ? '' : issue.path, issue.message)
        return false
      }
      return push(ctx, path, `does not match any allowed type (${options.map(o => o.json({ defs: ctx.defs, emitted: {} }).type ?? 'value').join(', ')})`)
    },
    json (jctx) {
      return withDescription({ anyOf: options.map(o => o.json(jctx)) }, schema)
    }
  }
  return describe(schema, description)
}

/** Discriminated union on a string property, e.g. node.type or chart.kind. */
export function oneOfBy (discriminator: string, variants: Record<string, Schema>, description?: string): Schema {
  const schema: Schema = {
    check (value, path, ctx) {
      if (typeOf(value) !== 'object') return push(ctx, path, `expected object, got ${typeOf(value)}`)
      const tag = (value as Record<string, unknown>)[discriminator]
      const variant = typeof tag === 'string' ? variants[tag] : undefined
      if (!variant) {
        return push(ctx, path ? `${path}.${discriminator}` : discriminator,
          `must be one of: ${Object.keys(variants).join(', ')}`)
      }
      return variant.check(value, path, ctx)
    },
    json (jctx) {
      return withDescription({
        oneOf: Object.values(variants).map(v => v.json(jctx)),
        'x-max-discriminator': discriminator
      }, schema)
    }
  }
  return describe(schema, description)
}

/** Named, possibly recursive reference. Register the target with define(). */
export function ref (name: string): Schema {
  return {
    check (value, path, ctx) {
      const target = ctx.defs[name]
      if (!target) return push(ctx, path, `internal: unknown schema ${name}`)
      if (ctx.depth >= ctx.maxDepth) return push(ctx, path, `nesting deeper than ${ctx.maxDepth} levels`)
      ctx.depth++
      try {
        return target.check(value, path, ctx)
      } finally {
        ctx.depth--
      }
    },
    json (jctx) {
      if (!(name in jctx.emitted)) {
        jctx.emitted[name] = null
        const target = jctx.defs[name]
        jctx.emitted[name] = target ? target.json(jctx) : {}
      }
      return { $ref: `#/$defs/${name}` }
    }
  }
}

export interface Validator {
  root: Schema
  defs: Record<string, Schema>
  maxDepth: number
}

export interface ValidationResult {
  valid: boolean
  issues: Issue[]
}

export function validateWith (validator: Validator, value: unknown, maxIssues = 50): ValidationResult {
  const ctx: Ctx = { issues: [], defs: validator.defs, depth: 0, maxDepth: validator.maxDepth, maxIssues }
  const ok = validator.root.check(value, '', ctx)
  return { valid: ok && ctx.issues.length === 0, issues: ctx.issues }
}

export function toJsonSchema (validator: Validator, meta: { id: string, title: string }): Record<string, unknown> {
  const jctx: JsonCtx = { defs: validator.defs, emitted: {} }
  const root = validator.root.json(jctx)
  return {
    $schema: 'https://json-schema.org/draft/2020-12/schema',
    $id: meta.id,
    title: meta.title,
    'x-max-depth': validator.maxDepth,
    ...root,
    $defs: jctx.emitted
  }
}
